import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  ShoppingCart, 
  User, 
  Package, 
  Truck, 
  CreditCard, 
  Search, 
  Plus, 
  Minus, 
  Trash2, 
  Check,
  ChevronDown,
  ArrowLeft,
  Mail,
  AlertCircle,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { 
  createAdminOrder, 
  selectCreatingOrder, 
  selectCreateOrderError 
} from "@/store/slices/orderSlice";
import { 
  fetchAdminProducts, 
  selectAdminProducts, 
  selectAdminProductsLoading 
} from "@/store/slices/productSlice";
import { useAdminAuth } from "@/context/Admin";
import { formatCurrency } from "@/utils/format";
import { cn } from "@/lib/utils";
import Loader from "@/components/loader/loader";
import { toast } from "sonner";

export default function CreateOrder() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getAllUsers } = useAdminAuth();

  // Redux state
  const products = useAppSelector(selectAdminProducts);
  const productsLoading = useAppSelector(selectAdminProductsLoading);
  const creatingOrder = useAppSelector(selectCreatingOrder);
  const createOrderError = useAppSelector(selectCreateOrderError);

  // Local state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  
  // Selected user
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);

  // Order items (cart)
  const [orderItems, setOrderItems] = useState([]);
  
  // Product selection
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);

  // Shipping address
  const [shippingAddress, setShippingAddress] = useState({
    recipient_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "Nigeria",
    phone: "",
  });

  // Order options
  const [shippingCost, setShippingCost] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [orderStatus, setOrderStatus] = useState("Processing");
  const [notes, setNotes] = useState("");
  const [sendNotification, setSendNotification] = useState(true);

  // Fetch products and users on mount
  useEffect(() => {
    dispatch(fetchAdminProducts());
    fetchUsers();
  }, [dispatch]);

  const fetchUsers = async (search = "") => {
    setUsersLoading(true);
    try {
      const data = await getAllUsers(1, 50, "active", search);
      setUsers(data.users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchTerm) {
        fetchUsers(userSearchTerm);
      } else {
        fetchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchTerm]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    const productList = Array.isArray(products) ? products : [];
    if (!productSearchTerm) return productList.slice(0, 20);
    const lower = productSearchTerm.toLowerCase();
    return productList
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(lower) ||
          p.product_id?.toLowerCase().includes(lower) ||
          p.productId?.toLowerCase().includes(lower) ||
          p.sku?.toLowerCase().includes(lower)
      )
      .slice(0, 20);
  }, [products, productSearchTerm]);

  // Calculate totals
  const orderTotals = useMemo(() => {
    const subtotal = orderItems.reduce((sum, item) => {
      const price = item.variation?.price || item.product.price;
      return sum + price * item.quantity;
    }, 0);
    
    // Tax is calculated on backend
    const total = subtotal + Number(shippingCost) - Number(discountAmount);
    
    return {
      subtotal,
      shippingCost: Number(shippingCost),
      discountAmount: Number(discountAmount),
      total: Math.max(0, total),
    };
  }, [orderItems, shippingCost, discountAmount]);

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserPopoverOpen(false);
    
    // Pre-fill shipping address if user has phone
    if (user) {
      setShippingAddress((prev) => ({
        ...prev,
        recipient_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        phone: user.phone_number || "",
      }));
    }
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    const productId = product.id || product.product_id;
    
    // Check if product already in cart
    const existingIndex = orderItems.findIndex(
      (item) => {
        const itemProductId = item.product.id || item.product.product_id;
        return itemProductId === productId && !item.variation;
      }
    );

    if (existingIndex >= 0) {
      // Increment quantity
      const newItems = [...orderItems];
      newItems[existingIndex].quantity += 1;
      setOrderItems(newItems);
    } else {
      // Add new item
      setOrderItems([
        ...orderItems,
        {
          product,
          variation: null,
          quantity: 1,
        },
      ]);
    }
    setProductPopoverOpen(false);
    setProductSearchTerm("");
  };

  // Handle variation selection for an item
  const handleVariationSelect = (itemIndex, variation) => {
    const newItems = [...orderItems];
    newItems[itemIndex].variation = variation;
    setOrderItems(newItems);
  };

  // Handle quantity change
  const handleQuantityChange = (index, delta) => {
    const newItems = [...orderItems];
    const newQty = newItems[index].quantity + delta;
    
    if (newQty <= 0) {
      newItems.splice(index, 1);
    } else {
      newItems[index].quantity = newQty;
    }
    
    setOrderItems(newItems);
  };

  // Remove item
  const handleRemoveItem = (index) => {
    const newItems = [...orderItems];
    newItems.splice(index, 1);
    setOrderItems(newItems);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    if (orderItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    if (!shippingAddress.recipient_name || !shippingAddress.address_line1 || !shippingAddress.city || !shippingAddress.state || !shippingAddress.country) {
      toast.error("Please fill in all required shipping address fields");
      return;
    }

    // Build order data
    const items = orderItems.map((item) => ({
      product_id: item.product.id || item.product.product_id,
      quantity: item.quantity,
      ...(item.variation && { variation_id: item.variation.id }),
    }));

    const orderData = {
      userId: selectedUser.id,
      items,
      shippingAddress,
      shippingCost: orderTotals.shippingCost,
      discountAmount: orderTotals.discountAmount,
      paymentMethod,
      paymentStatus,
      status: orderStatus,
      notes,
      sendNotification,
    };

    try {
      const result = await dispatch(createAdminOrder(orderData)).unwrap();
      navigate(`/admin/orders/${result.orderId}`);
    } catch (error) {
      // Error is handled in the thunk
      console.error("Failed to create order:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Create Order</h2>
            <p className="text-sm text-muted-foreground">
              Create a new order on behalf of a customer
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Select Customer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer
              </CardTitle>
              <CardDescription>
                Select the customer for this order
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {selectedUser ? (
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {selectedUser.first_name} {selectedUser.last_name}
                        <span className="text-muted-foreground">
                          ({selectedUser.email})
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        Select a customer...
                      </span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search by name, email, or phone..."
                      value={userSearchTerm}
                      onValueChange={setUserSearchTerm}
                    />
                    <CommandList>
                      {usersLoading ? (
                        <div className="p-4 text-center">
                          <Loader />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No customer found.</CommandEmpty>
                          <CommandGroup>
                            {users.map((user) => (
                              <CommandItem
                                key={user.id}
                                value={`${user.first_name} ${user.last_name} ${user.email}`}
                                onSelect={() => handleUserSelect(user)}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {user.first_name} {user.last_name}
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {user.email} • {user.phone_number}
                                  </span>
                                </div>
                                {selectedUser?.id === user.id && (
                                  <Check className="ml-auto h-4 w-4" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </CardContent>
          </Card>

          {/* Step 2: Add Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Products
              </CardTitle>
              <CardDescription>
                Add products to this order
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Product Search */}
              <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Plus className="h-4 w-4" />
                    Add Product
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onValueChange={setProductSearchTerm}
                    />
                    <CommandList>
                      {productsLoading ? (
                        <div className="p-4 text-center">
                          <Loader />
                        </div>
                      ) : filteredProducts.length === 0 ? (
                        <div className="py-6 text-center text-sm">No product found.</div>
                      ) : (
                        <CommandGroup>
                          {filteredProducts.map((product) => {
                            const productId = product.id || product.product_id;
                            const productImage = product.image || product.image_url;
                            const stockQty = product.stockQuantity ?? product.stock_quantity;
                            
                            return (
                              <CommandItem
                                key={productId}
                                value={String(productId)}
                                onSelect={() => handleProductSelect(product)}
                              >
                                <div className="flex items-center gap-3 w-full">
                                  {productImage && (
                                    <img
                                      src={productImage}
                                      alt={product.name}
                                      className="h-10 w-10 object-cover rounded"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{product.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {formatCurrency(product.price)}
                                      {stockQty !== undefined && (
                                        <span className="ml-2">
                                          Stock: {stockQty}
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Order Items Table */}
              {orderItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Variation</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderItems.map((item, index) => {
                      const price = item.variation?.price || item.product.price;
                      const total = price * item.quantity;
                      const productId = item.product.id || item.product.product_id;
                      const productImage = item.product.image || item.product.image_url;

                      return (
                        <TableRow key={`${productId}-${item.variation?.id || 'default'}-${index}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {productImage && (
                                <img
                                  src={productImage}
                                  alt={item.product.name}
                                  className="h-8 w-8 object-cover rounded"
                                />
                              )}
                              <span className="font-medium truncate max-w-[150px]">
                                {item.product.name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.product.variations?.length > 0 ? (
                              <Select
                                value={item.variation?.id?.toString() || ""}
                                onValueChange={(value) => {
                                  const variation = item.product.variations.find(
                                    (v) => v.id.toString() === value
                                  );
                                  handleVariationSelect(index, variation);
                                }}
                              >
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {item.product.variations.map((v) => (
                                    <SelectItem key={v.id} value={v.id.toString()}>
                                      {v.name} - {formatCurrency(v.price)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleQuantityChange(index, -1)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleQuantityChange(index, 1)}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(price)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(total)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No products added yet</p>
                  <p className="text-sm">Click "Add Product" to add items to this order</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Shipping Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Address
              </CardTitle>
              <CardDescription>
                Enter the delivery address
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="recipient_name">Recipient Name *</Label>
                  <Input
                    id="recipient_name"
                    value={shippingAddress.recipient_name}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, recipient_name: e.target.value })
                    }
                    placeholder="Full name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line1">Address Line 1 *</Label>
                  <Input
                    id="address_line1"
                    value={shippingAddress.address_line1}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, address_line1: e.target.value })
                    }
                    placeholder="Street address"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={shippingAddress.address_line2}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, address_line2: e.target.value })
                    }
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={shippingAddress.city}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, city: e.target.value })
                    }
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={shippingAddress.state}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, state: e.target.value })
                    }
                    placeholder="State"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">Postal Code</Label>
                  <Input
                    id="zip_code"
                    value={shippingAddress.zip_code}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, zip_code: e.target.value })
                    }
                    placeholder="Postal code"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    value={shippingAddress.country}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, country: e.target.value })
                    }
                    placeholder="Country"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={shippingAddress.phone}
                    onChange={(e) =>
                      setShippingAddress({ ...shippingAddress, phone: e.target.value })
                    }
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Order Summary */}
        <div className="space-y-6">
          {/* Payment & Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment & Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="paymentMethod">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="paystack">Paystack</SelectItem>
                    <SelectItem value="flutterwave">Flutterwave</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="paymentStatus">Payment Status</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="orderStatus">Order Status</Label>
                <Select value={orderStatus} onValueChange={setOrderStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Processing">Processing</SelectItem>
                    <SelectItem value="Shipped">Shipped</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div>
                <Label htmlFor="shippingCost">Shipping Cost (₦)</Label>
                <Input
                  id="shippingCost"
                  type="number"
                  min="0"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="discountAmount">Discount (₦)</Label>
                <Input
                  id="discountAmount"
                  type="number"
                  min="0"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="notes">Order Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes about this order..."
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sendNotification" className="cursor-pointer">
                    Send email to customer
                  </Label>
                </div>
                <Switch
                  id="sendNotification"
                  checked={sendNotification}
                  onCheckedChange={setSendNotification}
                />
              </div>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(orderTotals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span>{formatCurrency(orderTotals.shippingCost)}</span>
              </div>
              {orderTotals.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(orderTotals.discountAmount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{formatCurrency(orderTotals.total)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                * Tax will be calculated on the backend
              </p>

              {createOrderError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {createOrderError}
                </div>
              )}

              <Button
                className="w-full mt-4"
                size="lg"
                onClick={handleSubmit}
                disabled={creatingOrder || !selectedUser || orderItems.length === 0}
              >
                {creatingOrder ? (
                  <>
                    <Loader className="mr-2 h-4 w-4" />
                    Creating Order...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Create Order
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
